/**
 * chainPipeline — fullscreen-quad raymarcher for IQ's chain link SDF.
 *
 * This is a standalone pipeline used by the `★ Chain` scene. The 3D
 * raymarcher doesn't fit the 2D-SDF dispatch used by every other scene,
 * so it gets its own program + quad. The engine's per-frame loop calls
 * `drawChain()` when the active scene has `customDraw` set; that path
 * bypasses sceneFbo and postprocess and renders straight to the canvas.
 *
 * Attribution: chain SDF and main shading by Inigo Quilez, MIT licensed.
 * Source: https://iquilezles.org/articles/distfunctions (sdLink) and the
 * raymarched chain in https://www.shadertoy.com/view/Ml3fWj.
 */

import { linkProgram, POST_ATTRIBS } from "./glContext";

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;

// Inigo Quilez — MIT license — https://iquilezles.org/articles/distfunctions
// NOTE: the previous shader-local variant
//   vec3 q = vec3(p.x, p.y - clamp(p.y, -le, le), p.z);
// was wrong — it built a spindle (two cones meeting at a point on the
// central band) instead of a torus. The correct IQ form clamps |p.y|
// to a maximum and uses the (p.x, p.z) 2D radial distance as the
// in-plane coordinate, so the central band is a full ring at radius r1.
float sdLink(vec3 p, float le, float r1, float r2) {
  // In-plane radius: distance from the Y axis in the (p.x, p.z) plane.
  float radial = length(vec2(p.x, p.z));
  // Vertical offset from the link's central band, clamped to zero
  // when inside the band. q is (radial, p.y) for the tube centerline.
  vec2 q = vec2(radial - r1, max(abs(p.y) - le, 0.0));
  return length(q) - r2;
}

float mapChain(vec3 pos) {
  // Slide the chain upward so the camera sees fresh links come into view.
  pos.y += 0.5 * uTime;

  const float le = 0.13;
  const float r1 = 0.20;
  const float r2 = 0.09;

  vec3 a = pos; a.y = fract(a.y) - 0.5;
  vec3 b = pos; b.y = fract(b.y + 0.5) - 0.5;

  return min(sdLink(a, le, r1, r2), sdLink(b.zyx, le, r1, r2));
}

// Tetrahedral normal — IQ's https://iquilezles.org/articles/normalsSDF
vec3 calcNormal(vec3 pos) {
  const float eps = 0.0005;
  vec2 e = vec2(1.0, -1.0) * 0.5773;
  return normalize(
    e.xyy * mapChain(pos + e.xyy * eps) +
    e.yyx * mapChain(pos + e.yyx * eps) +
    e.yxy * mapChain(pos + e.yxy * eps) +
    e.xxx * mapChain(pos + e.xxx *eps)
  );
}

// Soft-shadow raymarch — IQ's https://iquilezles.org/articles/rmshadows
float calcSoftshadow(vec3 ro, vec3 rd, float mint, float tmax) {
  float res = 1.0;
  float t = mint;
  for (int i = 0; i < 16; i++) {
    float h = mapChain(ro + rd * t);
    res = min(res, 8.0 * h / t);
    t += clamp(h, 0.02, 0.10);
    if (res < 0.005 || t > tmax) break;
  }
  return clamp(res, 0.0, 1.0);
}

float calcOcclusion(vec3 pos, vec3 nor) {
  float occ = 0.0;
  float sca = 1.0;
  for (int i = 0; i < 5; i++) {
    float hr = 0.01 + 0.15 * float(i) / 4.0;
    vec3 aopos = nor * hr + pos;
    float dd = mapChain(aopos);
    occ += -(dd - hr) * sca;
    sca *= 0.95;
  }
  return clamp(1.0 - occ * 1.5, 0.0, 1.0);
}

void main() {
  // Camera orbits the chain once every ~9 seconds.
  // The chain scrolls up at 0.5*uTime, so both the camera and the target
  // offset by the same amount along Y to keep the chain stationary in
  // the view. We aim at a point that is at a link's CENTER PLANE
  // (ta.y = -0.5*uTime, same Y as the camera) so the ray always has
  // an X-Z component. The previous ta.y = 0.5 - 0.5*uTime put the target
  // at the "neck" between two links, and the camera-to-target ray
  // could miss the tubes entirely depending on the orbit angle.
  // Camera at a slight elevation looking horizontally at the chain. The
  // chain scrolls up at 0.5*uTime; we offset both the camera and the
  // target by 0.5*uTime so the chain stays stationary in the view and
  // the link tubes always sit in front of the camera. Without this
  // offset the chain scrolls out of frame after a few seconds.
  float an = 0.7 * uTime;
  vec3 ro = vec3(0.8 * cos(an), 0.3 - 0.5 * uTime, 0.8 * sin(an));
  vec3 ta = vec3(0.0, 0.5 - 0.5 * uTime, 0.0);

  // Build the camera basis (look-at without roll).
  vec3 ww = normalize(ta - ro);
  vec3 uu = normalize(cross(ww, vec3(0.0, 1.0, 0.0)));
  vec3 vv = cross(uu, ww);

  // vUV comes in as [-1, 1] across the screen; remap to NDC space with
  // a square pixel aspect so the chain doesn't stretch on portrait captures.
  vec2 p = vUV * vec2(uResolution.x / uResolution.y, 1.0);

  // Build the view ray. FOV factor 1.5 gives a 67deg FOV.
  vec3 rd = normalize(p.x * uu + p.y * vv + 1.5 * ww);

  // Sphere-trace the chain.
  const float tmax = 8.0;
  float t = 0.0;
  for (int i = 0; i < 128; i++) {
    vec3 pos = ro + t * rd;
    float h = mapChain(pos);
    if (h < 0.0001 || t > tmax) break;
    t += h;
  }

  // Shade the hit (or the sky).
  vec3 col = vec3(0.0);
  if (t < tmax) {
    vec3 pos = ro + t * rd;
    vec3 nor = calcNormal(pos);
    vec3 lig = normalize(vec3(0.6, 0.2, 0.4));
    vec3 hal = normalize(lig - rd);
    float dif = clamp(dot(nor, lig), 0.0, 1.0);
    float occ = calcOcclusion(pos, nor);
    if (dif > 0.001) dif *= calcSoftshadow(pos, lig, 0.01, 1.0);
    float spe = pow(clamp(dot(nor, hal), 0.0, 1.0), 16.0) * dif *
                (0.04 + 0.96 * pow(clamp(1.0 - dot(hal, -rd), 0.0, 1.0), 5.0));
    float amb = 0.5 + 0.5 * dot(nor, vec3(0.0, 1.0, 0.0));

    // Polished steel: cool ambient + warm diffuse + tight specular.
    col = vec3(0.5, 1.0, 1.2) * amb * occ;
    col += vec3(2.8, 2.2, 1.8) * dif;
    col *= 0.2;
    col += vec3(2.8, 2.2, 1.8) * spe * 3.0;
  }

  // Gamma — straight to sqrt, matches IQ's reference output.
  col = sqrt(col);
  outColor = vec4(col, 1.0);
}`;

export interface ChainPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  vbo: WebGLBuffer;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
}

export function createChainPipeline(gl: WebGL2RenderingContext): ChainPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  // Fullscreen triangle pair — two triangles covering clip space [-1, 1]².
  const verts = new Float32Array([
    -1, -1,
     1, -1,
    -1,  1,
    -1,  1,
     1, -1,
     1,  1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
  };
}

/** Draws the chain link raymarcher to the currently-bound framebuffer. */
export function drawChain(
  gl: WebGL2RenderingContext,
  pipe: ChainPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}